/**
 * Route Prefetching Utility
 * Preloads lazy-loaded route chunks on hover for faster navigation
 */

// Map of routes to their lazy import functions
const routeImports = {
  '/home': () => import('../components/pages/Home'),
  '/language': () => import('../components/pages/Language'),
  '/dictionary': () => import('../components/pages/Dictionary'),
  '/curriculum': () => import('../components/pages/Curriculum'),
  '/resources': () => import('../components/pages/Resources'),
  '/contact': () => import('../components/pages/Contact'),
};

// Track which routes have been prefetched to avoid duplicate requests
const prefetchedRoutes = new Set();

/**
 * Prefetch a route's chunk if not already prefetched
 * @param {string} path - The route path to prefetch
 */
export const prefetchRoute = (path) => {
  // Normalize path (remove hash, query params)
  const normalizedPath = path.split('#')[0].split('?')[0];
  
  if (prefetchedRoutes.has(normalizedPath)) {
    return; // Already prefetched
  }
  
  const importFn = routeImports[normalizedPath];
  if (importFn) {
    prefetchedRoutes.add(normalizedPath);
    // Use requestIdleCallback for non-blocking prefetch, fallback to setTimeout
    const schedulePreload = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    schedulePreload(() => {
      importFn().catch(() => {
        // Remove from set if prefetch fails so it can retry
        prefetchedRoutes.delete(normalizedPath);
      });
    });
  }
};

/**
 * Prefetch all routes (useful for preloading after initial page load)
 */
export const prefetchAllRoutes = () => {
  // Wait until the page is idle before prefetching all routes
  const schedulePreload = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
  schedulePreload(() => {
    Object.keys(routeImports).forEach(path => {
      if (!prefetchedRoutes.has(path)) {
        prefetchRoute(path);
      }
    });
  }, { timeout: 5000 }); // 5 second timeout
};

export default prefetchRoute;
